import { useCallback, useEffect, useRef, useState } from 'react';
import { agentApi, type BatchCall } from '@/shared/api/agentApi';
import { useSipStore } from '../stores/sipStore';
import { toast } from '@/components/ui/Toast';

/**
 * סבבים אוטומטיים בקמפיין preview: סבב ⇒ הכרעה ⇒ סבב הבא, בלי לחיצה.
 *
 * הרקע (07/09/2026, קמפיין 18): סבב שכל הקווים בו "לא ענה" פשוט קפא —
 * הפאנל הפסיק לתשאל, ואף אחד לא ירה סבב חדש. ה"חייגן האוטומטי" חייג
 * פעם אחת ונעצר. השרת אינו מחייג לבד ב-preview (בניגוד ל-progressive/
 * predictive), ולכן הלולאה חיה כאן, בדפדפן של הנציג.
 *
 * מכונת המצבים:
 *   off → dialing → ringing → (answered) in_call → [סיכום] → dialing …
 *                          └→ (אף אחד לא ענה) cooldown → dialing …
 *   dialing ─(422 no_leads)→ waiting (ממתינים ל-retry_at) → dialing …
 *                           → stopped (כולם טופלו / הנציג עצר / הקו לא רשום)
 *
 * 🔑 כל ההמשכיות האסינכרוניות נושאות `seq`; עצירה או התחלה מחדש מקדמות
 * אותו, וכל המשך ישן פשוט יוצא. `inFlightRef` מונע שני dial-batch במקביל
 * (StrictMode, אפקט שרץ פעמיים, לחיצה כפולה).
 */

export type RoundPhase = 'off' | 'dialing' | 'ringing' | 'in_call' | 'cooldown' | 'waiting' | 'stopped';

export type StopReason = 'exhausted' | 'manual' | 'paused' | 'sip' | 'window' | 'error' | 'campaign';

/** מרווחי הזמן — מיוצאים כדי שבדיקות יקצרו אותם. */
export const AUTO_ROUND_DELAYS = {
  /** בין סבב שלא נענה לסבב הבא. */
  cooldownMs: 3000,
  /** גבולות ההמתנה ל-retry_at. */
  waitMinMs: 5000,
  waitMaxMs: 60000,
  /** כשלידים "בטיפול" אצל מישהו אחר. */
  inFlightWaitMs: 30000,
  /** השרת עוד רואה שיחה פתוחה — כל כמה זמן לנסות שוב, וכמה פעמים. */
  onCallRetryMs: 3000,
  maxOnCallRetries: 10,
  /** backoff לשגיאות רשת/שרת. */
  errorBaseMs: 5000,
  errorMaxMs: 60000,
  maxConsecutiveErrors: 5,
};

/** מצבי סופטפון שבהם יש שיחה חיה (או מצלצלת). */
const SIP_BUSY = new Set(['on-call', 'on-hold', 'ringing-in', 'ringing-out']);

interface NoLeadsInfo {
  reason: 'waiting_retry' | 'in_flight' | 'exhausted';
  retryAt: Date | null;
  message: string;
}

/**
 * מפענח 422 של dial-batch. מעדיף את השדות המובנים (`code:'no_leads'` +
 * `reason`), ונופל ל-regex על ההודעה מול שרת ישן.
 */
export function parseDialBatchError(err: unknown): { kind: 'no_leads'; info: NoLeadsInfo } | { kind: 'on_call'; message: string } | { kind: 'blocked'; message: string } | { kind: 'error'; message: string } {
  const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = res?.data ?? {};
  const message = typeof data.message === 'string' ? data.message : 'החיוג נכשל';

  if (res?.status === 422) {
    // השרת עוד רואה את השיחה הקודמת פתוחה (הניתוק מגיע אליו שנייה אחרי
    // "שמור וסיים") — מצב חולף, לא סיבה לעצור את הסבבים.
    if (data.code === 'agent_on_call' || message.includes('אתה כבר בשיחה')) {
      return { kind: 'on_call', message };
    }
    if (data.code === 'no_leads' || message.includes('אין לידים')) {
      let reason: NoLeadsInfo['reason'] = 'exhausted';
      if (data.reason === 'waiting_retry' || data.reason === 'in_flight' || data.reason === 'exhausted') {
        reason = data.reason;
      } else if (message.includes('ממתינים לניסיון חוזר')) {
        reason = 'waiting_retry';
      } else if (message.includes('בטיפול')) {
        reason = 'in_flight';
      }
      let retryAt: Date | null = null;
      if (typeof data.retry_at === 'string' && !Number.isNaN(Date.parse(data.retry_at))) {
        retryAt = new Date(data.retry_at);
      } else {
        const m = message.match(/בשעה (\d{1,2}):(\d{2})/);
        if (m) {
          retryAt = new Date();
          retryAt.setHours(Number(m[1]), Number(m[2]), 0, 0);
          if (retryAt.getTime() < Date.now()) retryAt.setDate(retryAt.getDate() + 1);
        }
      }
      return { kind: 'no_leads', info: { reason, retryAt, message } };
    }
    return { kind: 'blocked', message };
  }
  if (res?.status === 403) return { kind: 'blocked', message };
  return { kind: 'error', message };
}

interface Options {
  /** מזהה הסבב החדש — ההורה כותב אותו ל-URL כדי שהפאנל יירשם עליו. */
  onBatch: (batch: string) => void;
}

export function useAutoRounds({ onBatch }: Options) {
  const [phase, setPhase] = useState<RoundPhase>('off');
  const [message, setMessage] = useState<string | null>(null);
  const [stopReason, setStopReason] = useState<StopReason | null>(null);
  const [batch, setBatchState] = useState<string | null>(null);
  const [answeredLeadId, setAnsweredLeadId] = useState<number | null>(null);
  const [nextAt, setNextAt] = useState<number | null>(null);

  const phaseRef = useRef<RoundPhase>('off');
  const batchRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const stopRef = useRef(true);
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failuresRef = useRef(0);
  const onCallRetriesRef = useRef(0);
  // מנוי ל-sipStore בזמן המתנה לסיום השיחה — מבוטל בכל עצירה/סבב.
  const waitCallUnsubRef = useRef<(() => void) | null>(null);
  // הודעת "מחוץ לשעות" פעם אחת לריצה — לא בכל סבב.
  const windowNoticedRef = useRef(false);
  // מזהה הקמפיין נמסר ב-start/adoptBatch ולא כ-prop: ההורה קורא start מיד
  // אחרי setActive, לפני שהרינדור הבא הספיק להעביר את הקמפיין החדש.
  const campaignRef = useRef<number | null>(null);
  const onBatchRef = useRef(onBatch);
  onBatchRef.current = onBatch;

  const go = useCallback((p: RoundPhase) => { phaseRef.current = p; setPhase(p); }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (waitCallUnsubRef.current) { waitCallUnsubRef.current(); waitCallUnsubRef.current = null; }
    setNextAt(null);
  }, []);

  const stop = useCallback((reason: StopReason, msg?: string | null) => {
    stopRef.current = true;
    seqRef.current += 1;
    clearTimer();
    setStopReason(reason);
    if (msg !== undefined) setMessage(msg);
    go('stopped');

    // 🔑 עצירת הלולאה כאן עוצרת רק את הסבב *הבא*. הסבב הנוכחי כבר יצא
    // לשרת ומצלצל אצל lines_per_agent לקוחות — הם ימשיכו לצלצל, ומי
    // שיענה יקבל שיחה שאיש לא מחכה לה. זו הסיבה ש"עצור" לא נראה כמו
    // עצירה. הניתוק בפועל נעשה בשרת.
    //
    // 'exhausted' הוא סיום טבעי (נגמרו הלידים) ולא בקשת עצירה — שם אין
    // מה לנתק, ואסור להשהות את הנציג בקמפיין.
    const id = campaignRef.current;
    if (id && reason !== 'exhausted') {
      void agentApi.dialerStopRounds(id).catch(() => {
        // כישלון ניתוק לא מחזיר את הלולאה לפעולה — היא עצורה בכל מקרה.
      });
    }
  }, [clearTimer, go]);

  const schedule = useCallback((ms: number, nextPhase: RoundPhase, fn: () => void) => {
    clearTimer();
    const seq = seqRef.current;
    go(nextPhase);
    setNextAt(Date.now() + ms);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setNextAt(null);
      if (seq !== seqRef.current || stopRef.current) return;
      fn();
    }, ms);
  }, [clearTimer, go]);

  const dialRound = useCallback(async () => {
    const id = campaignRef.current;
    if (!id || stopRef.current || inFlightRef.current) return;
    const seq = seqRef.current;

    // שערים לפני החיוג: בלי קו רשום השיחה שתיענה לא תגיע לאף אחד.
    // הנציג עדיין בשיחה (סיכם לפני שניתק) — הסבב הבא יוצא כשהיא נגמרת.
    // עד 28/09/2026 הסבב נורה מיד, השרת החזיר 422 "אתה כבר בשיחה"
    // והלולאה נעצרה ב"הסבבים נעצרו".
    if (SIP_BUSY.has(useSipStore.getState().state)) {
      clearTimer();
      go('waiting');
      setMessage('השיחה עדיין פתוחה — הסבב הבא ייצא כשהיא תסתיים.');
      waitCallUnsubRef.current = useSipStore.subscribe((st) => {
        if (SIP_BUSY.has(st.state)) return;
        if (waitCallUnsubRef.current) { waitCallUnsubRef.current(); waitCallUnsubRef.current = null; }
        if (seq !== seqRef.current || stopRef.current) return;
        setMessage(null);
        void dialRoundRef.current();
      });
      return;
    }
    // קו שנפל או שלוחה שנוצרה עכשיו — מנסים לרשום לפני שמוותרים.
    if (!useSipStore.getState().registered) {
      const ok = await useSipStore.getState().refreshLine();
      if (seq !== seqRef.current || stopRef.current) return;
      if (!ok) {
        stop('sip', 'הסופטפון אינו רשום — הסבבים נעצרו. כשהקו יתחבר, לחץ "המשך סבבים".');
        return;
      }
    }
    try {
      const st = await agentApi.dialerState();
      if (seq !== seqRef.current || stopRef.current) return;
      const c = st?.campaigns?.find((x) => x.id === id);
      if (c?.paused) { stop('paused', 'החייגן מושהה — הסבבים נעצרו.'); return; }
      // מחוץ לשעות הקמפיין (24/09/2026): נציג שמחייג בעצמו מקבל הודעה
      // וממשיך — החלון עוצר רק את החיוג האוטומטי של השרת.
      if (c && c.in_window === false && !windowNoticedRef.current) {
        windowNoticedRef.current = true;
        toast('מחוץ לשעות החיוג של הקמפיין — ממשיכים לחייג.', 'warn');
      }
    } catch {
      // מצב לא זמין אינו סיבה לא לחייג — השרת יחסום מה שצריך ב-422.
    }

    inFlightRef.current = true;
    go('dialing');
    setMessage(null);
    setAnsweredLeadId(null);
    try {
      const res = await agentApi.dialerDialBatch(id);
      // הנציג לחץ "עצור" בזמן שהסבב היה באוויר: השרת כבר חייג, ולכן לא
      // מספיק לזרוק את התשובה — צריך לנתק את מה שיצא. בלי זה נוצר בדיוק
      // המצב שהנציג דיווח עליו: לחצתי עצור, והטלפון של הלקוח ממשיך לצלצל.
      if (seq !== seqRef.current || stopRef.current) {
        void agentApi.dialerStopRounds(id).catch(() => {});
        return;
      }
      failuresRef.current = 0;
      onCallRetriesRef.current = 0;
      if (res?.window_notice && !windowNoticedRef.current) {
        windowNoticedRef.current = true;
        toast(res.window_notice, 'warn');
      }
      const b = res?.batch ?? null;
      if (!b) {
        stop('error', 'השרת לא החזיר מזהה סבב.');
        return;
      }
      batchRef.current = b;
      setBatchState(b);
      go('ringing');
      onBatchRef.current(b);
    } catch (err) {
      if (seq !== seqRef.current || stopRef.current) return;
      const parsed = parseDialBatchError(err);
      if (parsed.kind === 'no_leads') {
        const { reason, retryAt, message: msg } = parsed.info;
        setMessage(msg);
        if (reason === 'exhausted') { stop('exhausted', msg); return; }
        const ms = reason === 'in_flight'
          ? AUTO_ROUND_DELAYS.inFlightWaitMs
          : Math.min(AUTO_ROUND_DELAYS.waitMaxMs, Math.max(AUTO_ROUND_DELAYS.waitMinMs, (retryAt?.getTime() ?? 0) - Date.now()));
        schedule(ms, 'waiting', () => { void dialRound(); });
        return;
      }
      if (parsed.kind === 'on_call') {
        onCallRetriesRef.current += 1;
        if (onCallRetriesRef.current > AUTO_ROUND_DELAYS.maxOnCallRetries) {
          stop('error', `${parsed.message} (השרת עדיין רואה שיחה פתוחה — אם אין שיחה, לחץ "נתק" בסרגל העליון ואז "המשך סבבים".)`);
          return;
        }
        setMessage('ממתין שהשרת יסגור את השיחה הקודמת…');
        schedule(AUTO_ROUND_DELAYS.onCallRetryMs, 'waiting', () => { void dialRound(); });
        return;
      }
      if (parsed.kind === 'blocked') { stop('campaign', parsed.message); return; }
      failuresRef.current += 1;
      if (failuresRef.current >= AUTO_ROUND_DELAYS.maxConsecutiveErrors) {
        stop('error', `החיוג נכשל ${failuresRef.current} פעמים ברצף — הסבבים נעצרו. ${parsed.message}`);
        return;
      }
      setMessage(parsed.message);
      const ms = Math.min(AUTO_ROUND_DELAYS.errorMaxMs, AUTO_ROUND_DELAYS.errorBaseMs * 2 ** (failuresRef.current - 1));
      schedule(ms, 'cooldown', () => { void dialRound(); });
    } finally {
      inFlightRef.current = false;
    }
  }, [clearTimer, go, schedule, stop]);
  // ההמשך מתוך המנוי ל-sipStore קורא לגרסה העדכנית, לא לזו שנלכדה.
  const dialRoundRef = useRef(dialRound);
  dialRoundRef.current = dialRound;

  /** התחלה / המשך אחרי עצירה. */
  const start = useCallback((campaignId: number) => {
    campaignRef.current = campaignId;
    stopRef.current = false;
    seqRef.current += 1;
    failuresRef.current = 0;
    setStopReason(null);
    setMessage(null);
    void dialRound();
  }, [dialRound]);

  /**
   * "המשך סבבים" אחרי עצירה. "עצור סבבים" משהה את הנציג בקמפיין **בשרת**
   * (paused_at — כדי שהטיק של השרת לא יחייג לו), ושום מסלול לא ניקה את
   * ההשהיה: dialRound קרא paused=true ועצר מיד עם "החייגן מושהה", כך
   * ש"המשך" לא המשיך כלום (24/09/2026, קמפיין 26). המשך מפורש = ביטול
   * ההשהיה, ורק אז סבב.
   */
  const resume = useCallback((campaignId: number) => {
    campaignRef.current = campaignId;
    stopRef.current = false;
    seqRef.current += 1;
    const seq = seqRef.current;
    failuresRef.current = 0;
    setStopReason(null);
    setMessage(null);
    go('dialing');
    void agentApi.dialerPause(campaignId, false)
      .catch(() => { /* גם אם הביטול נכשל — השרת יחסום ב-422 אם צריך */ })
      .then(() => { if (seq === seqRef.current && !stopRef.current) void dialRound(); });
  }, [dialRound, go]);

  /** סבב שכבר נורה במקום אחר (למשל "חייג סבב" במסך הניהול) — מאמצים אותו. */
  const adoptBatch = useCallback((campaignId: number, b: string) => {
    campaignRef.current = campaignId;
    stopRef.current = false;
    seqRef.current += 1;
    failuresRef.current = 0;
    setStopReason(null);
    batchRef.current = b;
    setBatchState(b);
    go('ringing');
  }, [go]);

  /** הפאנל מדווח שהסבב הוכרע (פעם אחת לכל batch). */
  const onSettled = useCallback((b: string, calls: BatchCall[]) => {
    if (b !== batchRef.current) return;
    const answered = calls.find((c) => c.state === 'answered');
    // לקוח שענה נמצא על הקו גם אם הלולאה נעצרה בינתיים ("עצור סבבים" או
    // בקר ההשהיה באמצע הצלצול). הליד חייב לעלות לעבודה ולסיכום — אחרת
    // הנציג מדבר עם מישהו שהמסך מכריז עליו "הסבבים נעצרו" (24/09/2026).
    if (answered) {
      setAnsweredLeadId(answered.lead_id);
      if (!stopRef.current && phaseRef.current === 'ringing') go('in_call');
      return;
    }
    // לקוח ענה אבל השיחה לא הגיעה לסופטפון (לא רשום / שלוחה חדשה) — השרת
    // ניתק אותו. סבב נוסף ייגמר באותה צורה ויזרוק עוד לקוחות, ולכן עוצרים
    // ואומרים למה. עד כאן זה נראה "ענה" בלי שיחה ובלי "נתק" (28/09/2026).
    if (calls.some((c) => c.state === 'abandoned')) {
      stop('sip', 'לקוח ענה אבל השיחה לא הגיעה לסופטפון שלך, והוא נותק. בדוק שהקו מחובר (או רענן את הדף) ולחץ "המשך סבבים".');
      return;
    }
    if (stopRef.current || phaseRef.current !== 'ringing') return;
    schedule(AUTO_ROUND_DELAYS.cooldownMs, 'cooldown', () => { void dialRound(); });
  }, [dialRound, go, schedule, stop]);

  /** אחרי שהנציג סיכם את השיחה — הסבב הבא. */
  const afterWrapup = useCallback(() => {
    // הליד שענה סוכם — גם כשהלולאה עצורה, אחרת הבאנר נשאר "בשיחה".
    setAnsweredLeadId(null);
    if (stopRef.current) return;
    void dialRound();
  }, [dialRound]);

  // יציאה מהמסך = עצירה שקטה; אין מי שיראה את הסבב הבא.
  useEffect(() => () => {
    const wasRunning = !stopRef.current;
    const id = campaignRef.current;
    stopRef.current = true;
    seqRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    waitCallUnsubRef.current?.();
    // סגירת הטאב באמצע סבב השאירה קווים מצלצלים בלי אף אחד שיענה להם.
    if (wasRunning && id) void agentApi.dialerStopRounds(id).catch(() => {});
  }, []);

  return {
    phase, message, stopReason, batch, answeredLeadId, nextAt,
    active: !stopRef.current && phase !== 'off' && phase !== 'stopped',
    start, resume, stop, adoptBatch, onSettled, afterWrapup,
  };
}
